import { useMemo, useState } from "react";
import clsx from "clsx";
import { useSchedule, type ScheduleSlot } from "../api/hooks";
import { LoadingInline } from "./Spinner";

const WEEKDAY_LABELS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const FULL_WEEKDAYS = [
  "Dimanche",
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
];
const MONTHS = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];
const DAYS_AHEAD = 28;

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isSameDate(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatLong(d: Date) {
  return `${FULL_WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

function dateKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function TrialSlotPicker({
  gymSlug,
  value,
  onChange,
}: {
  gymSlug?: string;
  value?: string;
  onChange: (slotLabel: string | undefined, slotDate: string | undefined) => void;
}) {
  const { data: slots, isLoading } = useSchedule(gymSlug ?? "");

  const today = useMemo(() => startOfDay(new Date()), []);
  const minBookableDate = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 2);
    return d;
  }, [today]);

  const days = useMemo(() => {
    return Array.from({ length: DAYS_AHEAD }, (_, i) => {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      return d;
    });
  }, [today]);

  const slotsByDay = useMemo(() => {
    if (!slots) return new Map<string, ScheduleSlot[]>();
    const map = new Map<string, ScheduleSlot[]>();
    for (const day of days) {
      const dayOfWeek = day.getDay();
      const list = slots
        .filter((s) => s.dayOfWeek === dayOfWeek && !s.isClosure && s.format)
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
      map.set(dateKey(day), list);
    }
    return map;
  }, [slots, days]);

  // Premier jour réservable (>= J+2) qui a réellement des créneaux — le jour même,
  // le lendemain, ou un jour fermé (ex. dimanche) ne doit jamais être pré-sélectionné.
  const defaultSelectedDate = useMemo(() => {
    return (
      days.find((d) => d >= minBookableDate && (slotsByDay.get(dateKey(d)) ?? []).length > 0) ??
      minBookableDate
    );
  }, [days, slotsByDay, minBookableDate]);

  const [manualSelectedDate, setManualSelectedDate] = useState<Date | undefined>(undefined);
  const selectedDate = manualSelectedDate ?? defaultSelectedDate;

  if (!gymSlug) {
    return (
      <div className="rounded-md border border-dashed border-ink-300 bg-ink-100 p-4 text-sm text-ink-700">
        Sélectionne une salle au-dessus pour voir les créneaux disponibles.
      </div>
    );
  }

  if (isLoading) {
    return <LoadingInline label="Chargement des créneaux…" />;
  }

  const selectedDaySlots = slotsByDay.get(dateKey(selectedDate)) ?? [];

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-700">
          1. Choisis un jour
        </p>
        <div className="-mx-3 flex snap-x snap-mandatory gap-2 overflow-x-auto scroll-smooth px-3 pb-2 sm:mx-0 sm:px-0 [-webkit-overflow-scrolling:touch]">
          {days.map((d) => {
            const isActive = isSameDate(d, selectedDate);
            const isBookable = d >= minBookableDate;
            const hasSlots = isBookable && (slotsByDay.get(dateKey(d)) ?? []).length > 0;
            return (
              <button
                key={d.toISOString()}
                type="button"
                onClick={() => setManualSelectedDate(d)}
                disabled={!hasSlots}
                className={clsx(
                  "flex min-w-[3.5rem] shrink-0 snap-start flex-col items-center rounded-md border px-3 py-2.5 text-xs transition",
                  isActive
                    ? "border-brand-500 bg-brand-500 text-white"
                    : hasSlots
                      ? "border-ink-300 bg-white text-ink-700 hover:border-brand-500"
                      : "cursor-not-allowed border-ink-200 bg-ink-100 text-ink-300",
                )}
              >
                <span className="font-semibold uppercase">{WEEKDAY_LABELS[d.getDay()]}</span>
                <span className="text-base font-bold leading-tight">{d.getDate()}</span>
                <span className="text-[10px] uppercase">{MONTHS[d.getMonth()]?.slice(0, 4)}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-700">
          2. Choisis un créneau — {formatLong(selectedDate)}
        </p>
        {selectedDaySlots.length === 0 ? (
          <p className="text-sm text-ink-500">Aucun créneau ce jour-là.</p>
        ) : (
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            {selectedDaySlots.map((s) => {
              const label = `${formatLong(selectedDate)} · ${s.startTime}–${s.endTime} · ${s.format?.name}`;
              const isSelected = value === label;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() =>
                    onChange(
                      isSelected ? undefined : label,
                      isSelected ? undefined : dateKey(selectedDate),
                    )
                  }
                  className={clsx(
                    "rounded-md border-l-4 bg-white p-2 text-left text-sm transition hover:border-brand-500",
                    isSelected
                      ? "ring-2 ring-brand-500 ring-offset-1"
                      : "border-l-ink-300",
                  )}
                  style={{ borderLeftColor: isSelected ? "#DC2626" : (s.format?.color ?? "#cbd5e1") }}
                >
                  <div className="text-xs font-bold text-ink-700">
                    {s.startTime}–{s.endTime}
                  </div>
                  <div className="font-semibold">{s.format?.name}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {value && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-emerald-500 bg-emerald-50 p-3 text-sm">
          <span>
            <span className="font-semibold">Créneau souhaité :</span> {value}
          </span>
          <button
            type="button"
            onClick={() => onChange(undefined, undefined)}
            className="text-xs text-brand-500 hover:underline"
          >
            Réinitialiser
          </button>
        </div>
      )}
    </div>
  );
}
